import { IonIcon } from '@ionic/react';
import { ISlotData, TimeFrameValueEnum } from 'common/common';
import { TimeFrameValue } from 'common/types';
import { arrowBackOutline, arrowForwardOutline, close, lockClosedOutline } from 'ionicons/icons';
import SlotHeader from './SlotHeader';
import { getGameNameColor } from 'common/include';
import { heatBeanRatings } from 'config/HeatBeanRatings';
import { HeatBeanObjectKey } from 'config/config';
import TimeRangeButtons from './TimeRangeButtons';
import { useEffect, useRef } from 'react';
import { defaultTimeRanges, TimeRangeOption } from 'config/Timeframes';
import { insightAccessList, InsightValueEnum } from 'config/Insights';
import { playerTypeEnum } from 'common/commonEnum';
import { useHistory } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

interface SlotStatsCardProps {
  slotData: ISlotData;
  timeRanges?: TimeRangeOption[];
  activeRange?: TimeFrameValue;
  isProUser: boolean;
  onTimeRangeChange?: (range: TimeFrameValue) => void;
  onRefChange: (refValue: HTMLDivElement | null) => void;
  onBack?: () => void;
  onBookmark?: () => void;
  onFavorite?: () => void;
}

const SlotStatsCard: React.FC<SlotStatsCardProps> = ({
  slotData,
  timeRanges = defaultTimeRanges,
  activeRange = TimeFrameValueEnum.months1,
  isProUser,
  onTimeRangeChange,
  onRefChange,
  onBack,
}) => {
  const history = useHistory();
  const statsRef = useRef<HTMLDivElement>(null);

  const statsItem = [
    { label: 'RTP', key: InsightValueEnum.return, value: slotData.rtp, ntile: slotData.rtpNtile },
    {
      label: 'Jackpots',
      key: InsightValueEnum.jackpots,
      value: slotData.jackpots,
      ntile: slotData.jackpotsNtile,
    },
    {
      label: 'Spins',
      key: InsightValueEnum.spins,
      value: slotData.spins,
      ntile: slotData.spinsNtile,
    },
    {
      label: 'Play', //'Coin-in'
      key: InsightValueEnum.money_played,
      value: slotData.coinIn,
      ntile: slotData.coinInNtile,
    },
    {
      label: 'Win', //'Coin-out'
      key: InsightValueEnum.money_won,
      value: slotData.coinOut,
      ntile: slotData.coinOutNtile,
    },
    {
      label: 'VOL',
      key: InsightValueEnum.volatility_rating,
      value: slotData.volatility,
      ntile: slotData.volatilityNtile,
    },
    {
      label: 'W/S',
      key: InsightValueEnum.avg_money_won_per_spin,
      value: slotData.avgWinSpin,
      ntile: slotData.avgWinSpinNtile,
    },
    {
      label: 'W/S-J',
      key: InsightValueEnum.non_jkpt_won_per_spin,
      value: slotData.nonJackpotWinSpin,
      ntile: slotData.nonJackpotWinSpinNtile,
    },
    {
      label: 'J/S',
      key: InsightValueEnum.jackpot_value_per_spin,
      value: slotData.jackpotValuePerSpin,
      ntile: slotData.jackpotValuePerSpinNtile,
    },
    // {
    //   label: 'Win Rate',
    //   key: InsightValueEnum.win_rate,
    //   value: slotData.winRate,
    //   ntile: slotData.winRateNtile,
    // },
  ];

  const stats = statsItem.map((tf) => {
    const matchingAccess = insightAccessList.find((fa) => fa.value === tf.key);
    return {
      ...tf,
      ...(matchingAccess ? { playerTypeAccess: matchingAccess.playerTypeAccess } : {}),
    };
  });

  useEffect(() => {
    onRefChange(statsRef.current);
  }, [onRefChange]);

  const handleOptionClick = (isLocked: boolean) => {
    if (isLocked) {
      history.push('/auth/stripe', { previousPage: location.pathname });
    }
  };

  const scrollStats = (direction: 'left' | 'right') => {
    if (statsRef.current) {
      const scrollAmount = 240;
      statsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="slot-panel slot-stats">
      <div className="detail-close-row">
        <IonIcon
          icon={close}
          className="detail-close-icon"
          style={{ color: getGameNameColor(isProUser) }}
          onClick={onBack}
          role="button"
          aria-label="Back to EGM list"
        />
      </div>
      <SlotHeader slotData={slotData} onBack={onBack} isProUser={isProUser} />
      <div className="stats-scroll-container">
        <button className="scroll-arrow left" onClick={() => scrollStats('left')}>
          <IonIcon icon={arrowBackOutline} />
        </button>
        <div className="stats stats-scroll" ref={statsRef}>
          {stats.map((stat, idx) => {
            const { label, ntile, playerTypeAccess, value } = stat;
            const playerType = isProUser ? playerTypeEnum.slotCheckPro : playerTypeEnum.slotCheck;
            const isOptionAvailable = playerTypeAccess?.includes(playerType);
            const isLocked = isOptionAvailable ? false : true;
            const statClass = isLocked ? 'locked' : 'pro';
            const textColor = isLocked
              ? 'rgba(67, 71, 78, 0.5)'
              : [1, 2, 5, 6].includes(ntile)
              ? '#F8F9FF'
              : '#43474E';

            const backgroundColor = isLocked
              ? 'rgba(255, 255, 255, 0.5)'
              : heatBeanRatings[ntile as HeatBeanObjectKey]?.color;

            return (
              <div
                className={`stat ${statClass}`}
                key={idx}
                style={{ background: backgroundColor, color: textColor }}
                onClick={() => {
                  handleOptionClick(isLocked);
                }}
              >
                <div className="value">
                  {isLocked ? <FontAwesomeIcon icon={faLock} className="icon-lock" /> : value}
                </div>
                <div className="label">{label}</div>
              </div>
            );
          })}
        </div>

        <button className="scroll-arrow right" onClick={() => scrollStats('right')}>
          <IonIcon icon={arrowForwardOutline} />
        </button>
      </div>
      <TimeRangeButtons
        timeRanges={timeRanges}
        activeRange={activeRange}
        isProUser={isProUser}
        onTimeRangeChange={(val) => onTimeRangeChange?.(val)}
      />
    </div>
  );
};

export default SlotStatsCard;
